import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  ACCENT,
  SEMANTIC_COLOR_ERROR,
  SEMANTIC_COLOR_SUCCESS,
} from '@sama/design-tokens';
import { assert, isDefined } from '@sama/js-core';
import { snakeCase } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isNil } from 'lodash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WorkspaceLoadingService } from '../../../shared/misc-services/workspace-loading.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { RootKeyboardShortcutService } from '../../shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { TrainingTaskService } from '../../shared/misc-services/training-task.service';

@Component({
  selector: 'h3-training-tools',
  templateUrl: './training-tools.component.html',
  styleUrl: './training-tools.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TrainingToolsComponent extends DirectiveSuperclass {
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  isEvaluationMode: boolean;
  shouldDisableTools$: Observable<boolean>;
  isShowingHints$ = new BehaviorSubject(false);
  isLoading$ = new BehaviorSubject(false);
  trainingTaskService = inject(TrainingTaskService);
  calculated$ = this.trainingTaskService.hasScoreCalculated$();
  latestScore$ = this.buildLatestScore$();
  passMark = this.taskWorkspaceStore.state().task._extras.training?.pass_mark;
  color$ = this.trainingTaskService.hasPassingScore$().pipe(
    map((hasPassed) => {
      if (!isNil(hasPassed)) {
        return hasPassed ? SEMANTIC_COLOR_SUCCESS : SEMANTIC_COLOR_ERROR;
      }
      return ACCENT;
    }),
  );
  progress$ = combineLatest([this.latestScore$, this.color$]).pipe(
    map(([score, color]) => [{ percentage: Number(score), color }]),
  );
  private keyboardShortcutService = inject(RootKeyboardShortcutService);

  constructor() {
    super();
    const trainingExtras =
      this.taskWorkspaceStore.state().task._extras.training;
    assert(isDefined(trainingExtras), 'Training extras should be defined');
    this.isEvaluationMode = trainingExtras.type === 'EVALUATION';

    this.shouldDisableTools$ = combineLatest([
      inject(WorkspaceLoadingService).isLoading$,
      this.isLoading$,
    ]).pipe(
      map(([isLoading, isCalculatingScore]) => isCalculatingScore || isLoading),
    );
    this.setSubscriptionToShowHintsKeyboardEvents();
  }

  async calculateScore(action: string) {
    this.isLoading$.next(true);
    await this.trainingTaskService.calculateScore({
      id: snakeCase(action),
      source: 'button',
    });
    this.isLoading$.next(false);
  }

  async showHints(source = 'button') {
    this.isShowingHints$.next(true);
    await this.trainingTaskService.showHints({
      id: 'show_shapes',
      source,
    });
    this.isShowingHints$.next(false);
  }

  private buildLatestScore$() {
    return this.trainingTaskService
      .meanScore$()
      .pipe(map((score) => (score * 100).toFixed(2)));
  }

  private setSubscriptionToShowHintsKeyboardEvents() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('showHints'),
      async () => {
        if (this.isLoading$.value || this.isShowingHints$.value) {
          return;
        }
        await this.showHints('keyboard_shortcut');
      },
    );
  }
}
